import * as React from "react";
import { useState } from "react";
import { Box, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import HealthAndSafetyOutlinedIcon from "@mui/icons-material/HealthAndSafetyOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CloseIcon from "@mui/icons-material/Close";
import { useGetFieldAdminAlertsQuery, useSetFieldAdminAlertStateMutation } from "./fieldAdminApi";
import { CARD_BG, BORDER, AMBER } from "../../Global/colors";
import { WARN_YELLOW, BAD_RED, BAD_RED_TEXT, alertTimestamp } from "./designTokens";
import { FAPageState } from "./FAStates";
import { FAFilterChips, FAPageShell, FAPageHeader, FAEmptyState } from "./FALayout";

// alerts.html .alert-card.sev-* / .alert-icon.* — a left severity rule plus a tinted icon
// tile in the same tone.
// `action` is .alert-action / .alert-action.red / .alert-action.info — the button carries the
// severity's tone rather than a single amber for everything, so a red card reads as red all
// the way down. Amber's action tint is the app accent (#FDB940), not the icon's #FBBF24.
const SEVERITY = {
  amber: {
    rule: WARN_YELLOW, bg: "rgba(251,191,36,0.14)", color: WARN_YELLOW,
    action: { bg: "rgba(253,185,64,0.12)", border: "rgba(253,185,64,0.28)", color: AMBER, hover: "rgba(253,185,64,0.2)" },
  },
  red: {
    rule: BAD_RED, bg: "rgba(239,68,68,0.14)", color: BAD_RED_TEXT,
    action: { bg: "rgba(239,68,68,0.1)", border: "rgba(239,68,68,0.28)", color: BAD_RED_TEXT, hover: "rgba(239,68,68,0.18)" },
  },
  info: {
    rule: "#60A5FA", bg: "rgba(96,165,250,0.14)", color: "#60A5FA",
    action: { bg: "rgba(96,165,250,0.1)", border: "rgba(96,165,250,0.28)", color: "#93C5FD", hover: "rgba(96,165,250,0.18)" },
  },
};

// alerts.html .btn-text — the two header actions.
const BTN_TEXT_SX = {
  display: "inline-flex", alignItems: "center", gap: "5px",
  px: "12px", py: "8px", borderRadius: "7px",
  background: "transparent", border: 0, cursor: "pointer", font: "inherit",
  fontSize: "0.8rem", fontWeight: 600, color: "rgba(255,255,255,0.65)",
  "&:hover": { color: AMBER, backgroundColor: "rgba(253,185,64,0.06)" },
  "&:disabled": { opacity: 0.4, cursor: "default", color: "rgba(255,255,255,0.65)", backgroundColor: "transparent" },
};

const CATEGORY_ICON = {
  Team: GroupsOutlinedIcon,
  QE: FactCheckOutlinedIcon,
  Stock: Inventory2OutlinedIcon,
  Sites: PlaceOutlinedIcon,
  "H&S": HealthAndSafetyOutlinedIcon,
};

export default function Alerts() {
  const navigate = useNavigate();
  const { data, isSuccess, isError, error, refetch } = useGetFieldAdminAlertsQuery();
  const [setAlertState] = useSetFieldAdminAlertStateMutation();
  const [activeFilter, setActiveFilter] = useState("All");

  // Every one of these invalidates fa_alerts, so the feed and the top-bar badge (same cache
  // entry) both settle without a reload. Failures are swallowed deliberately: alert state is
  // a convenience, and an error toast over a dismissed card would be worse than the card
  // reappearing on the next refetch.
  const mark = (alert, action) => setAlertState({ action, key: alert.key });

  // Both bulk actions send the keys currently rendered. The server deliberately does not
  // re-derive the feed for these — that would race with what the user is looking at — so
  // omitting the keys is not "apply to everything", it is "apply to nothing", which is what
  // Mark all read did: it posted {action: "read_all"} alone and stamped zero alerts.
  const bulk = (action, list) => {
    const keys = list.map((a) => a.key);
    if (keys.length) setAlertState({ action, keys });
  };

  const openAlert = (alert) => {
    if (!alert.read) mark(alert, "read");
    navigate(alert.action_path);
  };

  const dismiss = (event, alert) => {
    event.stopPropagation();   // the whole card is the navigate target
    mark(alert, "clear");
  };

  if (!isSuccess) return <FAPageState isError={isError} error={error} onRetry={refetch} count={4} />;

  const alerts = data.alerts;
  // Tabs come from what the feed actually produced rather than a fixed list, so a
  // category with no source can't show as a permanently-empty tab.
  const categories = [...new Set(alerts.map((a) => a.category))];
  const unreadCount = data.unread_count ?? alerts.filter((a) => !a.read).length;
  const filters = ["All", ...categories];
  const counts = { All: alerts.length };
  categories.forEach((c) => { counts[c] = alerts.filter((a) => a.category === c).length; });

  const filtered = activeFilter === "All" ? alerts : alerts.filter((a) => a.category === activeFilter);

  return (
      <FAPageShell>
        <FAPageHeader
          title="Alerts"
          // The design's sub describes the feed rather than the team: how many are unread,
          // what feeds it, and that it is newest-first. The source list is the categories the
          // feed actually produced — the design names four it happens to mock, and hardcoding
          // those would advertise sources this team may have none of.
          sub={<>
            {unreadCount > 0 ? `${unreadCount} unread` : "All read"}
            {categories.length ? ` · ${categories.join(", ")}` : ""}
            {" — most recent first"}
          </>}
          action={
            <Box sx={{ display: "flex", gap: "6px" }}>
              <Box
                component="button"
                type="button"
                disabled={unreadCount === 0}
                onClick={() => bulk("read_all", alerts.filter((a) => !a.read))}
                sx={BTN_TEXT_SX}
              >
                Mark all read
              </Box>
              <Box
                component="button"
                type="button"
                disabled={alerts.length === 0}
                onClick={() => bulk("clear_all", alerts)}
                sx={BTN_TEXT_SX}
              >
                Clear
              </Box>
            </Box>
          }
        />

        {filters.length > 1 ? (
          <FAFilterChips filters={filters} counts={counts} active={activeFilter} onChange={setActiveFilter} />
        ) : null}

        {filtered.length === 0 ? (
          <FAEmptyState card py="70px">
              {alerts.length === 0 ? "Nothing needs attention right now." : "No alerts in this category."}
            </FAEmptyState>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
            {filtered.map((alert) => {
              const sev = SEVERITY[alert.severity] || SEVERITY.info;
              const Icon = CATEGORY_ICON[alert.category] || PlaceOutlinedIcon;
              return (
                <Box
                  key={alert.id}
                  onClick={() => openAlert(alert)}
                  sx={{
                    backgroundColor: CARD_BG,
                    border: `1px solid ${BORDER}`,
                    borderLeft: `3px solid ${sev.rule}`,
                    borderRadius: "12px",
                    px: "16px",
                    py: "14px",
                    display: "grid",
                    gridTemplateColumns: { xs: "36px 1fr auto", sm: "40px 1fr auto" },
                    gap: "14px",
                    alignItems: "flex-start",
                    cursor: "pointer",
                    opacity: alert.read ? 0.62 : 1,
                    transition: "border-color 0.15s, opacity 0.15s",
                    "&:hover": { borderColor: "rgba(255,255,255,0.12)", borderLeftColor: sev.rule },
                  }}
                >
                  <Box sx={{ width: { xs: "36px", sm: "40px" }, height: { xs: "36px", sm: "40px" }, borderRadius: "10px", backgroundColor: sev.bg, color: sev.color, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <Icon sx={{ fontSize: "1.2rem" }} />
                  </Box>

                  {/* .alert-body — title, sub, meta, action, stacked. */}
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: "0.94rem", fontWeight: 600, color: "#fff", letterSpacing: "-0.005em", lineHeight: 1.35 }}>
                      {alert.title}
                    </Typography>
                    <Typography sx={{ fontSize: "0.8rem", color: "rgba(255,255,255,0.6)", mt: "4px", lineHeight: 1.5 }}>
                      {alert.sub}
                    </Typography>

                    {/* .alert-meta — the category pill sits with the timestamp under the sub,
                        not beside the title, so the title line stays one clean statement. */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: "8px", mt: "9px", flexWrap: "wrap" }}>
                      <Box component="span" sx={{ fontSize: "0.62rem", fontWeight: 700, px: "8px", py: "3px", borderRadius: "4px", letterSpacing: "0.05em", textTransform: "uppercase", backgroundColor: sev.bg, color: sev.color }}>
                        {alert.category}
                      </Box>
                      {alert.occurred_at ? (
                        // Every alert is an aggregate, so this is the oldest thing inside it —
                        // "how long has this been waiting", not "when was this rendered".
                        <Typography title="Oldest item in this alert" component="span" sx={{ fontSize: "0.72rem", color: "rgba(255,255,255,0.45)" }}>
                          {alertTimestamp(alert.occurred_at)}
                        </Typography>
                      ) : null}
                    </Box>

                    {/* .alert-action — a button under the content, not a link floated right.
                        The old treatment was hidden below sm, which took the whole point of
                        the card away on the phone this app is actually used on. */}
                    {alert.action_label ? (
                      <Box
                        component="button"
                        type="button"
                        onClick={(event) => { event.stopPropagation(); openAlert(alert); }}
                        sx={{
                          display: "inline-flex", alignItems: "center", gap: "6px", mt: "11px",
                          px: "12px", py: "8px", borderRadius: "7px", cursor: "pointer",
                          font: "inherit", fontSize: "0.78rem", fontWeight: 600,
                          backgroundColor: sev.action.bg,
                          border: `1px solid ${sev.action.border}`,
                          color: sev.action.color,
                          transition: "background 0.15s",
                          "&:hover": { backgroundColor: sev.action.hover },
                        }}
                      >
                        <ArrowForwardIcon sx={{ fontSize: "0.85rem" }} />
                        {alert.action_label}
                      </Box>
                    ) : null}
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                    <Box
                      component="button"
                      type="button"
                      aria-label={`Dismiss ${alert.title}`}
                      onClick={(event) => dismiss(event, alert)}
                      sx={{
                        display: "flex", alignItems: "center", justifyContent: "center",
                        width: "26px", height: "26px", flexShrink: 0, borderRadius: "6px",
                        background: "none", border: "none", p: 0, cursor: "pointer",
                        color: "rgba(255,255,255,0.4)",
                        "&:hover": { backgroundColor: "rgba(255,255,255,0.05)", color: "#fff" },
                      }}
                    >
                      <CloseIcon sx={{ fontSize: "1rem" }} />
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}

        <Typography sx={{ fontSize: "0.72rem", color: "rgba(255,255,255,0.35)", mt: "18px", lineHeight: 1.5 }}>
          Alerts are derived live from attendance, the H&amp;S sequence, the QE queue, stock movements and the site
          pipeline — so they clear themselves as you action them. Dismissing one hides it until what it is reporting
          changes; if the same problem grows or returns, the alert comes back.
        </Typography>
      </FAPageShell>
  );
}
